import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Modal, Button } from 'antd'
import randall from './assets/randall.jpg'
import david from './assets/david.jpeg'
import micbg from './assets/micbg.png'
import LiveKitRoomClient, { CONNECTION_ERROR } from './LiveKitRoomClient'
import MicrophoneLayout from './MicrophoneLayout'
import { api } from '../../services'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import './styles.scss'
import { languages } from '../../constants'

const serverUrl = process.env.REACT_APP_LIVEKIT_SEERVER_URL
const COACHER_MAP = {
  Randall: { label: 'Randall Craig', image: randall },
  David: { label: 'David Nevin', image: david }
}
// ─── Error banner content map ─────────────────────────────────────────────────
const ERROR_META = {
  [CONNECTION_ERROR.MIC_DENIED]: {
    icon: '🎙️',
    title: 'Microphone access denied',
    retryable: false
  },
  [CONNECTION_ERROR.MIC_NOT_FOUND]: {
    icon: '🔇',
    title: 'No microphone detected',
    retryable: false
  },
  [CONNECTION_ERROR.NO_INTERNET]: {
    icon: '📡',
    title: 'No internet connection',
    retryable: true
  },
  [CONNECTION_ERROR.SERVER_UNREACHABLE]: {
    icon: '⚠️',
    title: 'Cannot reach server',
    retryable: true
  },
  [CONNECTION_ERROR.MAX_RETRIES_EXCEEDED]: {
    icon: '❌',
    title: 'Connection failed',
    retryable: true
  },
  [CONNECTION_ERROR.TOKEN_INVALID]: {
    icon: '🔐',
    title: 'Session expired',
    retryable: true
  },
  [CONNECTION_ERROR.UNKNOWN]: {
    icon: '⚠️',
    title: 'Connection error',
    retryable: true
  }
}

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ connectedToLiveKit, coachStatus, reconnectAttempt, t }) => {
  if (connectedToLiveKit === 'connecting') {
    return <span className="dot blue">🔵 {t('v4:connecting')}</span>
  }
  if (connectedToLiveKit === 'reconnecting') {
    return (
      <span className="dot orange">
        🟠 {t('v5:reconnecting') || 'Reconnecting'}
        {reconnectAttempt > 0 && ` (${reconnectAttempt}/5)`}
      </span>
    )
  }
  if (connectedToLiveKit === 'connected') {
    return coachStatus
      ? <span className="dot green">🟢 {t('v5:connected')}</span>
      : <span className="dot yellow">🟡 {t('v5:waiting_for_coach')}</span>
  }
  if (connectedToLiveKit === 'disconnected') {
    return <span className="dot red">🔴 {t('v5:disconnected')}</span>
  }
  return null
}

// ─── Error banner ─────────────────────────────────────────────────────────────
const ErrorBanner = ({ error, onRetry, isRetrying }) => {
  if (!error) return null
  const meta = ERROR_META[error.type] || ERROR_META[CONNECTION_ERROR.UNKNOWN]
  return (
    <div className="connection-error-banner">
      <div className="connection-error-banner__header">
        <span className="connection-error-banner__icon">{meta.icon}</span>
        <strong className="connection-error-banner__title">{meta.title}</strong>
      </div>
      <p className="connection-error-banner__message">{error.message}</p>
      {meta.retryable && onRetry && (
        <Button
          size="small"
          className="retry-btn"
          onClick={onRetry}
          loading={isRetrying}
          disabled={isRetrying}
        >
          {isRetrying ? 'Retrying…' : 'Try Again'}
        </Button>
      )}
    </div>
  )
}

// ─── CoachModal ───────────────────────────────────────────────────────────────
const CoachModal = ({ visible, onClose, selectedCoacher }) => {
  const [connectStatus, setConnectStatus] = useState(false)
  const [coachStatus, setCoachStatus] = useState(false)
  const [connectedToLiveKit, setConnectedToLiveKit] = useState(null)
  const [isSpeaking, setIsSpeaking] = useState(null)
  const [token, setToken] = useState(null)
  const [connectionError, setConnectionError] = useState(null)
  const [reconnectAttempt, setReconnectAttempt] = useState(0)
  const [isFetchingToken, setIsFetchingToken] = useState(false)
  const isLeavingRef = useRef(false)

  const active_organization_id = useSelector(
    state => state.user.info.active_organization_id
  )
  const { t } = useTranslation()

  // Derived booleans
  const isConnecting =
    connectedToLiveKit === 'connecting' ||
    connectedToLiveKit === 'reconnecting' ||
    isFetchingToken
  const isConnected = connectedToLiveKit === 'connected'

  // ── Shared reset: tears down the LiveKit session cleanly ─────────────────────
  const resetSession = useCallback(() => {
    isLeavingRef.current = true
    setConnectStatus(false)   // tells LiveKitRoomClient to disconnect
    setToken(null)
    setConnectedToLiveKit(null)
    setConnectionError(null)
    setCoachStatus(false)
    setIsSpeaking(null)
    setReconnectAttempt(0)
    setIsFetchingToken(false)
  }, [])

  // ── Stop session whenever the modal is closed (incl. page navigation) ──────
  useEffect(() => {
    if (visible) {
      isLeavingRef.current = false
    } else {
      // Modal closed — always kill the audio session
      resetSession()
    }
  }, [visible, resetSession])

  // ── Safety net: kill session on component unmount (e.g. page navigation) ────
  useEffect(() => {
    return () => {
      isLeavingRef.current = true
      // Directly flip the flag-state values; setState calls after unmount
      // are no-ops in React 18, so this is safe and avoids any warnings.
      setConnectStatus(false)
    }
  }, [])

  // ── Token fetch ───────────────────────────────────────────────────────────
  const fetchLiveKitToken = useCallback(async () => {
    if (isConnecting || isConnected) return
    setIsFetchingToken(true)
    setConnectionError(null)
    // Do NOT reset token to null here — that would trigger a spurious
    // [connectStatus, token] cycle that flashes 'disconnected' before
    // 'connecting' ever renders. The new token overwrites the old one on success.

    try {
      const languageKey = localStorage.getItem('i18nextLng') || "english"
      const language = languages.find(lang => lang.key === languageKey)?.name
      const res = await api.sharebot.fetchLiveKitToken(
        active_organization_id,
        selectedCoacher,
        language
      )

      if (isLeavingRef.current) return

      if (res.data && res.data.token) {
        setToken(res.data.token)
        setConnectStatus(true) // trigger connection immediately, even if token value is identical
      } else {
        throw new Error('Empty token in response')
      }
    } catch (err) {
      setConnectionError({
        type: CONNECTION_ERROR.TOKEN_INVALID,
        message: 'Failed to start the session. Please try again.'
      })
      setConnectedToLiveKit('disconnected')
    } finally {
      setIsFetchingToken(false)
    }
  }, [isConnecting, isConnected, active_organization_id, selectedCoacher])

  // ── Leave handler ─────────────────────────────────────────────────────────
  const handleLeave = () => {
    resetSession()
    setTimeout(onClose, 300)
  }

  // ── Mic click: probe permission first, then connect ───────────────────────
  const handleMicClick = async () => {
    if (isConnecting || isConnected) return

    // Quick permission probe — gives instant feedback before any network call
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true })
      probe.getTracks().forEach(t => t.stop())
    } catch (err) {
      const name = err && err.name ? err.name : ''
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setConnectionError({
          type: CONNECTION_ERROR.MIC_DENIED,
          message: 'Microphone access is blocked. Please enable it in your browser or system settings.'
        })
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        setConnectionError({
          type: CONNECTION_ERROR.MIC_NOT_FOUND,
          message: 'No microphone found. Please connect one and try again.'
        })
      } else {
        setConnectionError({
          type: CONNECTION_ERROR.MIC_DENIED,
          message: 'Could not access your microphone. Please check your settings.'
        })
      }
      return // Do not proceed to connect
    }

    if (isLeavingRef.current) return

    fetchLiveKitToken()
  }

  return (
    <Modal
      visible={visible}
      footer={null}
      closable={false}
      centered
      width={900}
      destroyOnClose={true}
      className="coach-modal"
    >
      {/* ── Status badge ── */}
      <div className="status-indicator">
        <StatusBadge
          connectedToLiveKit={connectedToLiveKit}
          coachStatus={coachStatus}
          reconnectAttempt={reconnectAttempt}
          t={t}
        />
      </div>

      {/* ── Coach image + intro ── */}
      <div className="main">
        <div className="main-intro">
          <p>
            {t('v5:coach_intro').replace(
              'DGF Logistic',
              COACHER_MAP[selectedCoacher]?.label || 'DGF Logistic'
            )}
            <br />
            {t('v5:coach_help')}
          </p>
        </div>
        <img
          src={COACHER_MAP[selectedCoacher]?.image}
          alt="Coach"
        />
      </div>

      {/* ── LiveKit client (no UI — pure logic) ── */}
      {visible && (
        <LiveKitRoomClient
          connectStatus={connectStatus}
          setConnectedToLiveKit={setConnectedToLiveKit}
          setIsSpeaking={setIsSpeaking}
          setCoachStatus={setCoachStatus}
          setConnectionError={setConnectionError}
          setReconnectAttempt={setReconnectAttempt}
          token={token}
          serverUrl={serverUrl}
        />
      )}

      {/* ── Error banner ── */}
      <ErrorBanner
        error={connectionError}
        onRetry={fetchLiveKitToken}
        isRetrying={isFetchingToken || connectedToLiveKit === 'reconnecting'}
      />

      {/* ── Mic section ── */}
      <div className="micimage">
        {!isConnecting && !isConnected && !connectionError && (
          <div className="mictext">
            <p>{t('v5:press_to_speak')}</p>
          </div>
        )}

        <div
          className={`mic ${isConnecting ? 'mic--disabled' : ''}`}
          onClick={handleMicClick}
          title={
            isConnecting
              ? connectedToLiveKit === 'reconnecting'
                ? 'Reconnecting…'
                : 'Connecting…'
              : isConnected
                ? 'Connected'
                : 'Click to start'
          }
        >
          <MicrophoneLayout
            connectedToLiveKit={connectedToLiveKit || 'idle'}
            isSpeaking={isSpeaking}
          />
        </div>

        <img src={micbg} alt="micbg" />
      </div>

      {/* ── Leave button ── */}
      <div className="leavebtn">
        <Button
          type="primary"
          size="large"
          style={{
            backgroundColor: '#6b7bff',
            borderRadius: 20,
            paddingLeft: 20,
            paddingRight: 20
          }}
          onClick={handleLeave}
        >
          {t('v5:leave')}
        </Button>
      </div>
    </Modal>
  )
}

export default CoachModal
